import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [marquee] - The marquee container.
 * @property {HTMLElement} [marqueeTrack] - The marquee track that holds duplicated groups.
 * @property {HTMLElement} [marqueeGroup] - The original group of blocks to clone.
 * @property {HTMLElement} [static] - The static container.
 * @property {HTMLElement} [closeButton] - The close/dismiss button.
 * @property {HTMLElement[]} [countdowns] - Countdown block elements.
 */

/**
 * A custom element that manages an announcement banner with marquee scrolling,
 * countdown timers, and dismissal.
 *
 * @extends Component<Refs>
 */
class AnnouncementBanner extends Component {
  /** @type {number | null} */
  #countdownInterval = null;
  #storageKey = '';

  connectedCallback() {
    super.connectedCallback();

    this.#storageKey = `announcement-banner-dismissed-${this.dataset.sectionId}`;

    // Check if already dismissed this session
    if (sessionStorage.getItem(this.#storageKey) === 'true') {
      this.hidden = true;
      return;
    }

    // Initialize marquee (clone content for infinite loop)
    if (this.dataset.displayMode === 'marquee') {
      this.#initMarquee();
    }

    // Initialize countdowns
    if (this.refs.countdowns?.length) {
      this.#initCountdowns();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }
  }

  /**
   * Dismiss the announcement banner with a smooth transition.
   */
  dismiss() {
    this.classList.add('announcement-banner--dismissing');
    sessionStorage.setItem(this.#storageKey, 'true');

    this.addEventListener('transitionend', () => {
      this.hidden = true;
    }, { once: true });

    // Fallback in case transitionend doesn't fire
    setTimeout(() => {
      this.hidden = true;
    }, 400);
  }

  /**
   * Clone the marquee group enough times to guarantee seamless infinite scrolling,
   * even when the content is narrower than the screen.
   *
   * Strategy:
   * 1. Measure the original group width.
   * 2. Determine how many copies are needed so the total track width is at least
   *    2× the container width (so there's always content filling the viewport
   *    while the first set scrolls out).
   * 3. Animate by exactly one group width (not -50%) using a CSS custom property,
   *    so the loop resets invisibly regardless of the number of copies.
   */
  #initMarquee() {
    const { marqueeTrack, marqueeGroup } = this.refs;
    if (!marqueeTrack || !marqueeGroup) return;

    // We need the group to be rendered to measure it, so use requestAnimationFrame.
    requestAnimationFrame(() => {
      const containerWidth = this.offsetWidth || window.innerWidth;
      const groupWidth = marqueeGroup.offsetWidth;

      if (groupWidth === 0) return;

      // We need enough copies so that:
      //   groupWidth × totalCopies >= 2 × containerWidth
      // Minimum 2 copies so the loop seam is always off-screen.
      const minCopies = Math.max(2, Math.ceil((2 * containerWidth) / groupWidth));

      // The original group already counts as 1 copy.
      const extraCopies = minCopies - 1;

      for (let i = 0; i < extraCopies; i++) {
        const clone = /** @type {Element} */ (marqueeGroup.cloneNode(true));
        clone.removeAttribute('ref');
        clone.setAttribute('aria-hidden', 'true');
        marqueeTrack.appendChild(clone);
      }

      // Set the animation to translate by exactly one group width so the loop
      // is seamless: when the track has moved one full group, it snaps back
      // to 0 and the animation restarts invisibly.
      marqueeTrack.style.setProperty('--marquee-translate', `-${groupWidth}px`);

      // Signal to CSS that cloning is complete — triggers the opacity reveal transition.
      // The track stays hidden (opacity: 0) until this attribute is set, preventing
      // any flash of uncloned or mis-sized content.
      this.setAttribute('data-marquee-ready', '');
    });
  }

  /**
   * Initialize all countdown timers and start a 1-second interval.
   */
  #initCountdowns() {
    const { countdowns } = this.refs;
    if (!countdowns?.length) return;

    // Initial update
    this.#updateCountdowns();

    // Update every second
    this.#countdownInterval = setInterval(() => {
      this.#updateCountdowns();
    }, 1000);
  }

  /**
   * Update all countdown elements with current remaining time.
   */
  #updateCountdowns() {
    const { countdowns } = this.refs;
    if (!countdowns?.length) return;

    const now = Date.now();

    for (const countdown of countdowns) {
      const endDateStr = countdown.dataset.endDate;
      if (!endDateStr) continue;

      const endDate = new Date(endDateStr).getTime();
      const diff = endDate - now;

      if (diff <= 0) {
        // Expired
        const hideWhenExpired = countdown.dataset.hideWhenExpired === 'true';
        if (hideWhenExpired) {
          countdown.classList.add('announcement-banner__countdown--expired');
        }

        this.#setCountdownValues(countdown, 0, 0, 0, 0);
        continue;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      this.#setCountdownValues(countdown, days, hours, minutes, seconds);
    }
  }

  /**
   * Set the countdown display values.
   *
   * @param {HTMLElement} element - The countdown block element.
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #setCountdownValues(element, days, hours, minutes, seconds) {
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

    const daysEl = element.querySelector('[data-unit="days"]');
    const hoursEl = element.querySelector('[data-unit="hours"]');
    const minutesEl = element.querySelector('[data-unit="minutes"]');
    const secondsEl = element.querySelector('[data-unit="seconds"]');

    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }
}

customElements.define('announcement-banner', AnnouncementBanner);
